/**
 * The backoffice's data migration, as viewer-api's `/api/admin/migration/runs` answers it (DATAT-603).
 *
 * The reads are migration-service's tables; the commands go to migration-service through the outbox, so
 * they answer `202` with a request id and take effect a moment later — the run's next version shows it.
 */

/**
 * A bulk copy of g3d's Datastore, group by group. Its catch-up, which follows g3d's events, is part of the run (not a
 * run of its own): see `MigrationCatchUp`.
 */
export type MigrationRunType = 'BULK';

export type MigrationRunState =
  | 'RUNNING'
  | 'PAUSING'
  | 'PAUSED'
  | 'STOPPING'
  | 'STOPPED'
  | 'DONE'
  | 'FAILED'
  /** Everything copied, but errors left that nobody acknowledged or reran: the operator's turn. */
  | 'DONE_WITH_ERRORS';

export type MigrationSegmentState = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';

export const MIGRATION_RUN_TYPES: MigrationRunType[] = ['BULK'];
export const MIGRATION_RUN_STATES: MigrationRunState[] = [
  'RUNNING',
  'PAUSING',
  'PAUSED',
  'STOPPING',
  'STOPPED',
  'DONE',
  'DONE_WITH_ERRORS',
  'FAILED',
];
export const MIGRATION_SEGMENT_STATES: MigrationSegmentState[] = [
  'PENDING',
  'RUNNING',
  'DONE',
  'FAILED',
];

/**
 * Nothing will change without someone starting a new run. `DONE_WITH_ERRORS` is not final: its errors wait for an
 * acknowledge or a rerun.
 */
export function isFinalRunState(state: MigrationRunState): boolean {
  return state === 'DONE' || state === 'STOPPED' || state === 'FAILED';
}

export type MigrationPage<T> = {
  items: T[];
  total: number;
  pages: number;
  /** Zero-based. */
  page: number;
  size: number;
};

export type MigrationSegmentCounts = {
  total: number;
  pending: number;
  running: number;
  done: number;
  failed: number;
};

/** Entities read from Datastore, written to PostgreSQL, and skipped (recorded as errors or out of scope). */
export type MigrationCounters = {
  read: number;
  written: number;
  skipped: number;
};

/** Whether a run's catch-up follows its source's events; its only command is stop. */
export type MigrationCatchUpState = 'FOLLOWING' | 'STOPPING' | 'STOPPED';

/**
 * What a run's catch-up does now: it piles the changes while the run copies (and again during a retry, a replay or a
 * rerun), applies them once the run is done, then applies them as they come. A stopped one regenerates the item
 * versions it brought (`STOPPING`) before it is `STOPPED`.
 */
export type MigrationCatchUpPhase = 'PILING' | 'APPLYING' | 'LIVE' | 'STOPPING' | 'STOPPED';

/** A change named by g3d's events: waiting, applied, refused (it would undo work done here), or failed and retried. */
export type MigrationCatchUpStatus = 'PENDING' | 'APPLIED' | 'CONFLICT' | 'FAILED';

export const MIGRATION_CATCH_UP_STATUSES: MigrationCatchUpStatus[] = [
  'PENDING',
  'FAILED',
  'CONFLICT',
  'APPLIED',
];

/** A bulk run's catch-up: the changes its source's events named since the run started, and what came of them. */
export type MigrationCatchUp = {
  state: MigrationCatchUpState;
  phase: MigrationCatchUpPhase;
  stoppedAt: string | null;
  /** An operator's e-mail, `the run's stop, by <email>`, or `run <id> started by <email>`. */
  stoppedBy: string | null;
  counts: { kind: string; status: MigrationCatchUpStatus; count: number }[];
  /** The backlog: `PENDING` and `FAILED`. */
  pending: number;
  /** Tried and failed: retried automatically (30 s, doubling, at most 1 h), never given up. */
  failed: number;
  /** Refused: applying them would undo work done on the new platform. */
  conflicts: number;
  applied: number;
  oldestPending: string | null;
  lastApplied: string | null;
  /** Changes named in the last 10 minutes. */
  arrivedRecently: number;
  /** Changes applied in the last 10 minutes. */
  appliedRecently: number;
};

/** One change a catch-up recorded, as g3d's events named it (several times, maybe). */
export type MigrationCatchUpChange = {
  /** Spelled as a key, e.g. `LabelTask 201 / LabelTaskItem "i"`. */
  entity: string;
  kind: string;
  status: MigrationCatchUpStatus;
  operation: string;
  firstSeenAt: string;
  seenAt: string;
  timesSeen: number;
  appliedAt: string | null;
  attempts: number;
  /** A failed change is not tried again before. */
  notBefore: string | null;
  /** Why a conflict was refused, or the last failure. */
  error: string | null;
};

export type MigrationRun = {
  id: number;
  requestId: string;
  type: MigrationRunType;
  groups: string[];
  state: MigrationRunState;
  /** Segments copied at once; `null` is no limit. */
  maxWorkers: number | null;
  requestedBy: string | null;
  requestedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** Moves with the run's transitions only; the page polls a mark of everything it shows instead. */
  version: number;
  segments: MigrationSegmentCounts;
  counters: MigrationCounters;
  /** Errors nobody has acknowledged yet. */
  unacknowledgedErrors: number;
  /** `null` when the run's source events are not followed. */
  catchUp: MigrationCatchUp | null;
  /** The g3d environment it copies from: `test`, `qa`, `prod`. */
  source: string;
  /** What the page shows for it: `g3d test`; null when migration-service no longer lists it. */
  sourceLabel: string | null;
  /** The source's BigQuery datasets the run was started without; it copies every other one. */
  bigqueryExcluded: string[];
  /** The W3C trace id of the operation's work: 32 lowercase hex characters; null when unknown. */
  traceId?: string | null;
  /** A Grafana Explore URL of the operation's logs, built by viewer-api; null when unknown. */
  logsUrl?: string | null;
};

/** A g3d environment migration-service may copy from: dev and testing offer test, QA and prod, prod its own. */
export type MigrationSource = {
  key: string;
  label: string;
  /** Its events are followed: a bulk run from it has a catch-up. */
  catchUp: boolean;
  /** Its BigQuery datasets as migration-service last listed them; null when this platform copies none. */
  bigquery: MigrationBigQueryDatasets | null;
};

/** What a bulk run over every group copies from BigQuery: every one of `datasets`, but those its start leaves out. */
export type MigrationBigQueryDatasets = {
  /** By name; empty when never listed. */
  datasets: { name: string; location: string | null }[];
  listedAt: string | null;
  /** Why the last listing failed (a missing grant): a run from the source cannot start until it is fixed. */
  error: string | null;
};

export type MigrationKindProgress = {
  kind: string;
  step: number;
  segmentsDone: number;
  read: number;
  written: number;
  skipped: number;
};

export type MigrationGroupProgress = {
  group: string;
  order: number;
  segments: MigrationSegmentCounts;
  kinds: MigrationKindProgress[];
};

/** A copy Google runs for the run (a bucket transfer, a BigQuery copy), polled by migration-service. */
export type MigrationStep = {
  id: number;
  type: string;
  source: string;
  externalId: string | null;
  state: string;
  bytes: number;
  objects: number;
  error: string | null;
  polledAt: string | null;
};

export type MigrationRunDetails = {
  run: MigrationRun;
  groups: MigrationGroupProgress[];
  steps: MigrationStep[];
};

export type MigrationCursor = {
  kind: string;
  step: number;
  done: boolean;
  read: number;
  written: number;
  skipped: number;
};

export type MigrationSegment = {
  id: number;
  group: string;
  /** Spelled as a key, e.g. `Patient "a7…"`; `null` is the start of the key space. */
  rangeStart: string | null;
  /** `null` is the end of the key space. */
  rangeEnd: string | null;
  state: MigrationSegmentState;
  claimedBy: string | null;
  heartbeatAt: string | null;
  error: string | null;
  cursors: MigrationCursor[];
};

export type MigrationError = {
  id: number;
  segmentId: number | null;
  kind: string;
  entityKey: string;
  /** `DANGLING_REFERENCE`, `INCOMPLETE`, `DUPLICATE_UID`, `UNREADABLE`, `CONFLICT` */
  reason: string;
  /** This entity's own account, with its names and keys. */
  detail: string | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  /**
   * The entities it stands for: an orphan is recorded once per missing parent, keyed by what is missing, its
   * first child in `detail`; 1 for any other error.
   */
  occurrences: number;
};

/**
 * A run's errors of one kind and reason: one group on the page, where a common cause shows at once and is
 * acknowledged or rerun in one command. The kinds come with the most unacknowledged entities first, each kind's
 * reasons the same way.
 */
export type MigrationErrorGroup = {
  kind: string;
  reason: string;
  /** Its rows; `unacknowledged` of them nobody has acknowledged. */
  errors: number;
  unacknowledged: number;
  /** The entities they stand for (an orphan's row counts its children); `unacknowledgedOccurrences` those unacknowledged. */
  occurrences: number;
  unacknowledgedOccurrences: number;
  lastRecordedAt: string;
};

/**
 * What a kind's counts say: every one equal (`SAME`); all read, the skipped exactly the errors recorded, the table holding
 * what was written (`EXPLAINED`); a comparison failing (`DIFFERS`, see `checks` and the kind's `note`); left in g3d on
 * purpose (`NOT_MIGRATED`); a count still due (`COUNTING`); the run still copying (`COPYING`).
 */
export type MigrationCountStatus = 'SAME' | 'EXPLAINED' | 'DIFFERS' | 'NOT_MIGRATED' | 'COUNTING' | 'COPYING';

/**
 * A kind of a whole bulk run, its counts side by side: Datastore's when the run started, the copiers' read, written and
 * skipped, the errors recorded, and the rows its table holds once the run is over.
 */
export type MigrationKindCount = {
  kind: string;
  /** The groups that copy it ("ITEMS, PASSES" for a kind both copy). */
  group: string;
  /** The table it maps to; null when it is not migrated. */
  targetTable: string | null;
  /** Why its counts differ by design, when they do. */
  note: string | null;
  expected: number | null;
  /** `COUNT`, or `STATISTICS`: Datastore's daily statistics, as of `expectedAt`. */
  expectedMethod: 'COUNT' | 'STATISTICS' | null;
  expectedAt: string | null;
  expectedError: string | null;
  /** Null for a kind no copier reads. */
  read: number | null;
  written: number | null;
  skipped: number | null;
  errors: number;
  inPostgres: number | null;
  inPostgresAt: string | null;
  inPostgresError: string | null;
  status: MigrationCountStatus;
  /** The comparisons that fail, when `DIFFERS`. */
  checks: string[];
};

export const MIGRATION_COUNT_STATUS_LABELS: Record<MigrationCountStatus, string> = {
  SAME: 'Same',
  EXPLAINED: 'Explained by errors',
  DIFFERS: 'Differs',
  NOT_MIGRATED: 'Not migrated',
  COUNTING: 'Counting',
  COPYING: 'Copying',
};

/** What a reason means, for the operator deciding whether to fix the source and rerun, or acknowledge. */
export const MIGRATION_ERROR_REASONS: Record<string, string> = {
  DANGLING_REFERENCE:
    'A key path or a property names a row the target does not have: Datastore never enforced references.',
  INCOMPLETE: 'A property the target cannot do without is missing.',
  DUPLICATE_UID:
    'What the target holds once, found twice (a UID under two parents): the second is not copied over the first.',
  UNREADABLE: 'A value the platform could not read: a legacy shape, a blob that is not JSON.',
  CONFLICT:
    'A change from g3d that would contradict newer data on the new platform: not applied, someone decides.',
};

/**
 * The answer to the poll: a mark of everything the page shows of the run (its progress, segments, errors,
 * backlog), and the run itself only when the mark differs from the one sent.
 */
export type MigrationChanges = {
  mark: string;
  run: MigrationRun | null;
};

export type MigrationRunsQuery = {
  page?: number;
  size?: number;
  type?: MigrationRunType;
  state?: MigrationRunState;
};

export type MigrationSegmentsQuery = {
  page?: number;
  size?: number;
  group?: string;
  states?: MigrationSegmentState[];
};

export type MigrationCatchUpChangesQuery = {
  page?: number;
  size?: number;
  statuses?: MigrationCatchUpStatus[];
  kind?: string;
};

export type MigrationErrorsQuery = {
  page?: number;
  size?: number;
  kind?: string;
  reason?: string;
  acknowledged?: boolean;
};

/**
 * Which errors an acknowledge or a rerun acts on — only ever those nobody acknowledged: some by id, or a group as
 * criteria (a kind, a reason, the ids left out of it), resolved to rows by migration-service when it applies the
 * command. Nothing named is every error of the run.
 */
export type MigrationErrorSelection =
  | { errorIds: number[] }
  | { kind?: string; reason?: string; excludedIds?: number[] };

/** What every command answers: the id migration-service will log it under (and a started run carries). */
export type MigrationRequested = {
  requestId: string;
};

export type MigrationCommand =
  | {
      type: 'START';
      runType: MigrationRunType;
      groups?: string[];
      source?: string;
      /** The source's BigQuery datasets not copied, for a bulk run over every group. */
      bigqueryExcluded?: string[];
    }
  | { type: 'PAUSE' | 'RESUME' | 'STOP'; runId: number }
  /** Stops the run's catch-up alone, at the freeze: changes after it are not applied. */
  | { type: 'STOP_CATCH_UP'; runId: number }
  | { type: 'RETRY'; runId: number; segmentId?: number }
  | { type: 'REPLAY'; runId: number; group?: string; segmentId?: number }
  | { type: 'THROTTLE'; runId: number; maxWorkers: number | null }
  /** Accepted as they are: nothing is copied, and the run closes `DONE` once none is left. */
  | { type: 'ACKNOWLEDGE'; runId: number; errors: MigrationErrorSelection }
  /** Read again: the segments and steps that recorded them; what is still wrong is reported again. */
  | { type: 'RERUN'; runId: number; errors: MigrationErrorSelection };
